import { useEffect, useState, useCallback } from "react";
import { ScanLine, Plug } from "lucide-react";
import { theme, card, emptyFrame, inputStyle, btnPrimary } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { RaiseIncidentCell, useIncidentSourceLinks } from "./resilience/IncidentSources";
import { ConnectorTrendPanel } from "../components/ConnectorTrendPanel";

interface SentinelSummary {
  total:          number;
  active:         number;
  newCount:       number;
  closed:         number;
  highSeverity:   number;
  mediumSeverity: number;
}

interface SentinelStatus {
  connected:      boolean;
  tenantId:       string | null;
  clientId:       string | null;
  subscriptionId: string | null;
  resourceGroup:  string | null;
  workspaceName:  string | null;
  lastSync:       string | null;
  lastError?:     string | null;
  entityCoverage?: { withEntities: number; total: number };
  summary:        SentinelSummary;
}

interface SentinelIncident {
  id:             string;
  incidentId:     string;
  title:          string;
  status:         string;
  severity:       string;
  description:    string | null;
  incidentNumber: number | null;
  occurredAt:     string;
  closedAt:       string | null;
  syncedAt:       string;
}

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  High:          { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  Medium:        { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  Low:           { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  Informational: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  New:    { bg: "var(--c-accentBg)",  text: "var(--c-accentText)" },
  Active: { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  Closed: { bg: "var(--c-successBg)", text: "var(--c-successText)" },
};

function Pill({ label, colors }: { label: string; colors: { bg: string; text: string } }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 3,
      fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export function ConnectSentinelPage() {
  const [status,   setStatus]   = useState<SentinelStatus | null>(null);
  const [incidents, setIncidents] = useState<SentinelIncident[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [syncing,  setSyncing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus,   setFilterStatus]   = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");

  const [form, setForm] = useState({
    tenantId: "", clientId: "", clientSecret: "",
    subscriptionId: "", resourceGroup: "", workspaceName: "",
  });
  // Which incidents already have a register entry (Operational Resilience), and whether this member may raise one.
  const incidentLinks = useIncidentSourceLinks(!!status?.connected);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<SentinelStatus>("/sentinel/status");
      setStatus(data);
      if (data.connected) {
        const params = new URLSearchParams();
        if (filterStatus   !== "all") params.set("status",   filterStatus);
        if (filterSeverity !== "all") params.set("severity", filterSeverity);
        const { incidents: incs } = await api.get<{ incidents: SentinelIncident[] }>(
          `/sentinel/incidents?${params}`
        );
        setIncidents(incs);
      }
    } catch {
      setError("Failed to load Sentinel status.");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/sentinel/connect", form);
      setShowForm(false);
      setForm({ tenantId: "", clientId: "", clientSecret: "", subscriptionId: "", resourceGroup: "", workspaceName: "" });
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      await api.post("/sentinel/sync", {});
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Remove Microsoft Sentinel integration and delete all synced incident data?")) return;
    setDisconnecting(true);
    try {
      await api.del("/sentinel/connect");
      setIncidents([]);
      await loadStatus();
    } catch {
      setError("Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) return <LoadingBlock label="Loading Sentinel status…" />;
  if (!status) return <div style={{ color: "var(--c-dangerText)", padding: "20px" }}>{error || "Failed to load status"}</div>;
  const s = status;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <ScanLine size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>
              Microsoft Sentinel
            </h2>
            {s.connected && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>
                Connected
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Ingest live security incidents from your Azure Sentinel workspace into AI compliance checks and evidence logs.
          </p>
        </div>
        {s.connected && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button onClick={handleSync} disabled={syncing}
              style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, opacity: syncing ? 0.6 : 1, cursor: syncing ? "not-allowed" : "pointer" }}>
              {syncing ? "Syncing…" : "Sync Now"}
            </button>
            <button onClick={handleDisconnect} disabled={disconnecting}
              style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: disconnecting ? 0.6 : 1 }}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Not connected — prompt */}
      {!s.connected && !showForm && (
        <div style={{ ...card, textAlign: "center", padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>
            Connect Microsoft Sentinel
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 560, margin: "0 auto 20px", lineHeight: 1.6 }}>
            Create an app registration in your Entra ID tenant, grant it the
            <strong> Microsoft Sentinel Reader</strong> role on your Log Analytics workspace,
            then enter the details below. Security incidents sync on demand and feed directly
            into your AI compliance checks.
          </p>
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13, cursor: "pointer" }}>
            Enter Details
          </button>
        </div>
      )}

      {/* Connect form */}
      {!s.connected && showForm && (
        <div style={{ ...card }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
            Sentinel Workspace Details
          </h3>
          <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 18px", lineHeight: 1.6 }}>
            In your Azure portal: <strong>App registrations → New registration</strong>, then assign
            the <strong>Microsoft Sentinel Reader</strong> role on the Log Analytics workspace under
            <strong> Access control (IAM)</strong>. Copy values from the app's Overview page.
          </p>
          <form onSubmit={handleConnect} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {[
              { key: "tenantId",       label: "Directory (Tenant) ID",    ph: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text" },
              { key: "clientId",       label: "Application (Client) ID",  ph: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text" },
              { key: "clientSecret",   label: "Client Secret (Value)",    ph: "Secret value from Azure",              type: "password" },
              { key: "subscriptionId", label: "Subscription ID",          ph: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx", type: "text" },
              { key: "resourceGroup",  label: "Resource Group Name",      ph: "my-sentinel-rg",                       type: "text" },
              { key: "workspaceName",  label: "Log Analytics Workspace",  ph: "my-sentinel-workspace",                type: "text" },
            ].map(({ key, label, ph, type }) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                <span style={{ fontWeight: 500, color: theme.color.text }}>{label}</span>
                <input
                  type={type}
                  value={form[key as keyof typeof form]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder={ph}
                  required
                  style={{ ...inputStyle, fontFamily: key !== "clientSecret" ? "monospace" : undefined, fontSize: 13 }}
                />
              </label>
            ))}
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving}
                style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Validating & Connecting…" : "Connect"}
              </button>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connected */}
      {s.connected && (
        <>
          {/* Summary tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Total Incidents",   value: s.summary.total,        bg: theme.color.panel,           text: theme.color.heading },
              { label: "Active",             value: s.summary.active,       bg: "var(--c-warningBg)",        text: "var(--c-warningText)" },
              { label: "New",                value: s.summary.newCount,     bg: "var(--c-accentBg)",         text: "var(--c-accentText)" },
              { label: "Closed",             value: s.summary.closed,       bg: "var(--c-successBg)",        text: "var(--c-successText)" },
              { label: "High Severity",      value: s.summary.highSeverity, bg: "var(--c-dangerBg)",         text: "var(--c-dangerText)" },
              { label: "Medium Severity",    value: s.summary.mediumSeverity, bg: "var(--c-warningBg)",      text: "var(--c-warningText)" },
            ].map((stat) => (
              <div key={stat.label} style={{ ...card, background: stat.bg, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: stat.text }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: stat.text, marginTop: 2, opacity: 0.8 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Continuous monitoring: the daily readings over 90 days */}
          <ConnectorTrendPanel connector="sentinel" />

          {/* Meta info */}
          <div style={{ ...card, padding: "12px 16px", marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12, color: theme.color.muted }}>
            <span>Workspace: <strong style={{ color: theme.color.text }}>{s.workspaceName}</strong></span>
            <span>Resource group: <strong style={{ color: theme.color.text }}>{s.resourceGroup}</strong></span>
            <span>Last sync: <strong style={{ color: theme.color.text }}>{fmtDate(s.lastSync)}</strong></span>
            {s.entityCoverage && <span title="Incidents whose hosts, accounts, IPs and applications have been fetched — what the resilience register uses to suggest the assets an incident hit">Entities: <strong style={{ color: theme.color.text }}>{s.entityCoverage.withEntities} of {s.entityCoverage.total} incidents</strong></span>}
          </div>
          {s.lastError && <div style={{ ...card, padding: "12px 16px", marginBottom: 20, fontSize: 12, color: "var(--c-dangerText)", background: "var(--c-dangerBg)" }}>Entity fetch refused on the last sync — {s.lastError}</div>}

          {/* Filters */}
          <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 6 }}>
              {["all", "New", "Active", "Closed"].map((f) => {
                const active = filterStatus === f;
                return (
                  <button key={f} onClick={() => setFilterStatus(f)} style={{
                    padding: "5px 13px", borderRadius: 3, fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                    background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                    color: active ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                  }}>
                    {f === "all" ? "All Status" : f}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {["all", "High", "Medium", "Low", "Informational"].map((f) => {
                const active = filterSeverity === f;
                return (
                  <button key={f} onClick={() => setFilterSeverity(f)} style={{
                    padding: "5px 13px", borderRadius: 3, fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
                    background: active ? "var(--c-accentBg)" : "var(--c-panel)",
                    color: active ? "var(--c-accentText)" : theme.color.muted, cursor: "pointer",
                  }}>
                    {f === "all" ? "All Severity" : f}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Incidents table */}
          {incidents.length === 0 ? (
            <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.muted, fontSize: 13 }}>
              No incidents match this filter.
            </div>
          ) : (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                    {["#", "Title", "Status", "Severity", "Occurred", "Incident"].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((inc, i) => (
                    <tr key={inc.id} style={{ borderBottom: i < incidents.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, fontFamily: "monospace", fontSize: 12 }}>
                        {inc.incidentNumber ?? "—"}
                      </td>
                      <td style={{ padding: "10px 14px", fontWeight: 500, color: theme.color.heading, maxWidth: 340 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inc.title}</div>
                        {inc.description && (
                          <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 400, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {inc.description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <Pill label={inc.status} colors={STATUS_COLORS[inc.status] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <Pill label={inc.severity} colors={SEVERITY_COLORS[inc.severity] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" }} />
                      </td>
                      <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>
                        {fmtDate(inc.occurredAt)}
                      </td>
                      <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                        <RaiseIncidentCell kind="sentinel" sourceId={inc.id} links={incidentLinks.links} canRaise={incidentLinks.canRaise} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
